import React, { useState } from 'react';
import {
  Modal,
  Typography,
  Box,
  Button,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  backgroundColor: '#ffffff',
  color: '#000000',
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  maxHeight: '90vh',
  overflowY: 'auto',
  border: '1px solid #ccc',
  borderRadius: theme.spacing(1),
}));

const ConflictItem = styled(Box)(({ theme }) => ({
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: '#fafafa',
}));

const ComparisonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const DataBox = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1.5),
  border: '1px solid #ddd',
  borderRadius: theme.spacing(0.5),
  backgroundColor: '#fff',
}));

const ExistingDataBox = styled(DataBox)(({ theme }) => ({
  borderColor: '#ff9800',
  backgroundColor: '#fff3e0',
}));

const NewDataBox = styled(DataBox)(({ theme }) => ({
  borderColor: '#2196f3',
  backgroundColor: '#e3f2fd',
}));

const ExcelConflictResolutionModal = ({ 
  isOpen, 
  onClose, 
  conflicts, 
  onResolve 
}) => {
  const [resolutionStrategy, setResolutionStrategy] = useState('skip'); // skip, replace, merge
  const [selectedConflicts, setSelectedConflicts] = useState({});

  const handleStrategyChange = (event) => {
    setResolutionStrategy(event.target.value);
    // Strateji değiştiğinde tüm çakışmaları bu strateji ile işaretle
    const newSelections = {};
    conflicts.forEach((conflict, index) => {
      newSelections[index] = event.target.value;
    });
    setSelectedConflicts(newSelections);
  };

  const handleIndividualChange = (index, strategy) => {
    setSelectedConflicts(prev => ({
      ...prev,
      [index]: strategy
    }));
  };

  const handleResolve = () => {
    const resolutions = conflicts.map((conflict, index) => ({
      ...conflict,
      resolution: selectedConflicts[index] || 'skip'
    }));
    onResolve(resolutions);
  };

  const getStrategyIcon = (strategy) => {
    switch (strategy) {
      case 'skip':
        return <CancelIcon color="warning" />;
      case 'replace':
        return <SwapHorizIcon color="primary" />;
      case 'merge':
        return <CheckCircleIcon color="success" />;
      default:
        return <WarningIcon />;
    }
  };

  const getStrategyColor = (strategy) => {
    switch (strategy) {
      case 'skip':
        return 'warning';
      case 'replace':
        return 'primary';
      case 'merge':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Modal 
      open={isOpen} 
      onClose={() => {}} 
      disableEscapeKeyDown={true}
    >
      <ModalBox>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <WarningIcon color="warning" sx={{ fontSize: 32 }} />
          <Typography variant="h5" component="h2">
            ⚠️ Çakışma Tespit Edildi
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>{conflicts.length} kayıt çakışma tespit edildi.</strong><br />
          Bu kayıtlar için nasıl işlem yapmak istediğinizi seçin.
        </Alert>

        {/* Cari Kasa Uyarısı - Değiştir seçeneği seçildiğinde göster */}
        {(resolutionStrategy === 'replace' || Object.values(selectedConflicts).some(s => s === 'replace')) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <strong>⚠️ ÖNEMLİ UYARI - CARİ KASA ETKİSİ</strong><br />
            "Değiştir" seçeneğini kullandığınızda, mevcut müşterinin <strong>tüm cari kasa geçmişi ve borç bilgileri silinecektir</strong>.<br />
            Bu işlem geri alınamaz! Eğer müşterinin ödenmemiş borçları varsa, bu bilgiler kaybolacaktır.<br />
            <strong>Devam etmeden önce müşterinin cari kasa durumunu kontrol etmeniz önerilir.</strong>
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>📊 Çakışma Çözüm Stratejileri</AlertTitle>
          <Typography variant="body2" component="div">
            <strong>⚠️ Atla:</strong> Mevcut kayıt değişmez, Excel'deki yeni veri göz ardı edilir<br/>
            <strong>🔄 Değiştir:</strong> Mevcut kayıt silinir, yerine Excel'deki yeni veri kaydedilir<br/>
            <strong>🔗 Birleştir:</strong> Mevcut kayıttaki boş alanlar Excel'deki verilerle doldurulur (dolu alanlar korunur)
          </Typography>
        </Alert>

        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
          <FormLabel component="legend">
            <Typography variant="h6" gutterBottom>
              🎯 Genel Çözüm Stratejisi:
            </Typography>
          </FormLabel>
          <RadioGroup
            value={resolutionStrategy}
            onChange={handleStrategyChange}
            row
          >
            <FormControlLabel
              value="skip"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <CancelIcon color="warning" />
                  <span>Atla (Mevcut kayıtları koru)</span>
                </Box>
              }
            />
            <FormControlLabel
              value="replace"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <SwapHorizIcon color="primary" />
                  <span>Değiştir (Yeni veri ile güncelle)</span>
                </Box>
              }
            />
            <FormControlLabel
              value="merge"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <span>Birleştir (Boş alanları doldur)</span>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <Typography variant="h6" gutterBottom>
          📋 Çakışan Kayıtlar:
        </Typography>

        <List sx={{ maxHeight: 500, overflow: 'auto', mb: 3 }}>
          {conflicts.map((conflict, index) => (
            <ConflictItem key={index}>
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  📋 Çakışma #{index + 1}
                </Typography>
                <Chip 
                  label={conflict.reason} 
                  color="warning" 
                  size="small" 
                />
              </Box>

              <ComparisonBox>
                <ExistingDataBox>
                  <Typography variant="subtitle2" fontWeight="bold" color="#f57c00" mb={1}>
                    🗃️ Mevcut Kayıt
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Cari Kod:</strong> {conflict.existingData?.cariCode || 'Boş'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Müşteri Adı:</strong> {conflict.existingData?.['Müşteri Adı'] || 'Boş'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Telefon:</strong> {conflict.existingData?.Telefon || 'Boş'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>E-posta:</strong> {conflict.existingData?.['E-posta'] || 'Boş'}
                  </Typography>
                </ExistingDataBox>

                <NewDataBox>
                  <Typography variant="subtitle2" fontWeight="bold" color="#1976d2" mb={1}>
                    📥 Yeni Veri (Excel)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Cari Kod:</strong> {conflict.cariCode || 'Boş'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Müşteri Adı:</strong> {conflict.musteriAdi || 'Boş'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Telefon:</strong> {conflict.telefon || 'Boş'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>E-posta:</strong> {conflict.email || 'Boş'}
                  </Typography>
                </NewDataBox>
              </ComparisonBox>

              <Box display="flex" justifyContent="center" mt={2}>
                <FormControl component="fieldset">
                  <Typography variant="body2" fontWeight="bold" mb={1} textAlign="center">
                    ⚙️ Bu kayıt için seçim:
                  </Typography>
                  <RadioGroup
                    value={selectedConflicts[index] || 'skip'}
                    onChange={(e) => handleIndividualChange(index, e.target.value)}
                    row
                  >
                    <FormControlLabel
                      value="skip"
                      control={<Radio size="small" />}
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CancelIcon color="warning" fontSize="small" />
                          <span style={{ fontSize: '0.875rem' }}>Atla</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="replace"
                      control={<Radio size="small" />}
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <SwapHorizIcon color="primary" fontSize="small" />
                          <span style={{ fontSize: '0.875rem' }}>Değiştir</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="merge"
                      control={<Radio size="small" />}
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CheckCircleIcon color="success" fontSize="small" />
                          <span style={{ fontSize: '0.875rem' }}>Birleştir</span>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              
              {index < conflicts.length - 1 && <Divider sx={{ mt: 2 }} />}
            </ConflictItem>
          ))}
        </List>

        <Box display="flex" gap={2} justifyContent="space-between">
          <Box display="flex" gap={1}>
            <Typography variant="body2" color="textSecondary">
              Özet: 
            </Typography>
            <Chip 
              label={`${Object.values(selectedConflicts).filter(s => s === 'skip').length} Atlanacak`}
              color="warning" 
              size="small" 
            />
            <Chip 
              label={`${Object.values(selectedConflicts).filter(s => s === 'replace').length} Değiştirilecek`}
              color="primary" 
              size="small" 
            />
            <Chip 
              label={`${Object.values(selectedConflicts).filter(s => s === 'merge').length} Birleştirilecek`}
              color="success" 
              size="small" 
            />
          </Box>
          
          <Box display="flex" gap={2}>
            <Button variant="outlined" onClick={onClose}>
              İptal
            </Button>
            <Button
              variant="contained"
              onClick={handleResolve}
              startIcon={<CheckCircleIcon />}
            >
              Çözümü Uygula
            </Button>
          </Box>
        </Box>
      </ModalBox>
    </Modal>
  );
};

export default ExcelConflictResolutionModal; 